// Import custom transition functions from the Timedoor module.
import { slideUp, slideDown, slideToggle, fadeOut, fadeIn, fadeToggle } from './modules/transitions';

// Import specific Bootstrap components for modular use in the project.
import Offcanvas from 'bootstrap/js/dist/offcanvas';
import Modal from 'bootstrap/js/dist/modal';
import Tabs from 'bootstrap/js/dist/tab';

/**
 * Coding Standards
 * ===========================
 * 1. Functionality Splitting:
 *    - Create distinct, single-purpose functions to improve readability and reusability.
 * 2. Modular Imports:
 *    - Import only the required modules to reduce bundle size and enhance maintainability.
 * 3. Event Listeners:
 *    - Always wrap initialization code inside `DOMContentLoaded` to ensure the DOM is fully loaded.
 */

// ── Language Dropdown ──────────────────────────────────────────────────────
// Wires up a toggle button + dropdown pair.
// Returns { btn, dropdown } or null if elements are missing.
function initDropdown(btnId, dropdownId) {
    const btn      = document.getElementById(btnId);
    const dropdown = document.getElementById(dropdownId);
    if (!btn || !dropdown) return null;

    btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = dropdown.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', isOpen);
        dropdown.setAttribute('aria-hidden', !isOpen);
    });

    // Prevent bubbling so the document-level close handler doesn't fire.
    dropdown.addEventListener('click', (e) => e.stopPropagation());

    return { btn, dropdown };
}

function closeDropdown({ btn, dropdown }) {
    dropdown.classList.remove('is-open');
    btn.setAttribute('aria-expanded', 'false');
    dropdown.setAttribute('aria-hidden', 'true');
}

// ── Mobile Hamburger + Drawer ──────────────────────────────────────────────
function initMobileDrawer() {
    const hamburgerBtn = document.getElementById('hamburgerBtn');
    const mobileDrawer = document.getElementById('mobileDrawer');

    if (!hamburgerBtn || !mobileDrawer) return null;

    hamburgerBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = mobileDrawer.classList.toggle('is-open');
        hamburgerBtn.setAttribute('aria-expanded', isOpen);
        hamburgerBtn.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
        mobileDrawer.setAttribute('aria-hidden', !isOpen);

        const icon = hamburgerBtn.querySelector('.iconify');
        if (icon) {
            icon.setAttribute('data-icon', isOpen
                ? 'material-symbols-light:close-rounded'
                : 'solar:hamburger-menu-linear'
            );
        }
    });

    // Close drawer when a nav link is tapped.
    mobileDrawer.querySelectorAll('.mobile-drawer__link').forEach((link) => {
        link.addEventListener('click', () => {
            mobileDrawer.classList.remove('is-open');
            hamburgerBtn.setAttribute('aria-expanded', 'false');
            hamburgerBtn.setAttribute('aria-label', 'Open menu');
            mobileDrawer.setAttribute('aria-hidden', 'true');
            const icon = hamburgerBtn.querySelector('.iconify');
            if (icon) icon.setAttribute('data-icon', 'solar:hamburger-menu-linear');
        });
    });

    return { hamburgerBtn, mobileDrawer };
}

function initScrollSpy() {
    const menu = document.querySelector('.navbar__menu');
    if (!menu) return;

    const items = Array.from(menu.querySelectorAll('.navbar__item'));

    // Map each menu item to its matching on-page section (if any).
    const entries = items
        .map((item) => {
            const link = item.querySelector('.navbar__link');
            if (!link) return null;

            const href = link.getAttribute('href') || '';
            const hash = href.includes('#') ? href.slice(href.indexOf('#') + 1) : '';
            let section = hash ? document.getElementById(hash) : null;

            // The bare "Home" link (home URL, no fragment) maps to the hero
            // section so it underlines while the hero is in view.
            if (!section && !hash) {
                const url = new URL(link.href, window.location.href);
                if (url.pathname === window.location.pathname) {
                    section = document.getElementById('hero');
                }
            }

            return section ? { item, section } : null;
        })
        .filter(Boolean);

    // Non-home page: no fragment links resolve to sections → clear any active
    // class WordPress added server-side and bail out.
    if (!entries.length) {
        items.forEach((item) => item.classList.remove('navbar__item--active'));
        return;
    }

    const navbar = document.querySelector('.navbar');

    // Clear all items (incl. WordPress's server-side `current-menu-item`, e.g.
    // HOME) so the underline on the home page is driven solely by scroll-spy.
    function setActive(activeItem) {
        items.forEach((item) => {
            item.classList.toggle('navbar__item--active', item === activeItem);
        });
    }

    function update() {
        // Reference line sits just below the fixed navbar.
        const offset = (navbar ? navbar.offsetHeight : 0) + 1;
        // Default to the first item so it stays underlined while at the top.
        let activeItem = entries[0].item;

        // Last section whose top has crossed the reference line wins.
        entries.forEach(({ item, section }) => {
            if (section.getBoundingClientRect().top <= offset) {
                activeItem = item;
            }
        });

        setActive(activeItem);
    }

    let ticking = false;
    function onScroll() {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
            update();
            ticking = false;
        });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update(); // set initial state before any scroll
}

// ── Navbar Height (CSS var) ────────────────────────────────────────────────
// Keeps `--navbar-height` in sync with the actual rendered navbar so layout
// (page offsets, mobile drawer position, scroll-spy) never drifts from the
// hand-tuned vw/px estimate in layout.scss as content, fonts, or breakpoints
// change. CSS keeps its value as the pre-JS fallback; this overrides it via
// inline style (which always wins over the `:root` rule) once measured.
function initNavbarHeight() {
    const navbars = [
        document.querySelector('.navbar'),
        document.querySelector('.navbar-mobile'),
    ].filter(Boolean);

    if (!navbars.length) return;

    function update() {
        // Whichever navbar is hidden (display: none) reports 0, so the
        // visible one's height wins.
        const height = navbars.reduce((max, el) => Math.max(max, el.offsetHeight), 0);
        if (height > 0) {
            document.documentElement.style.setProperty('--navbar-height', `${height}px`);
        }
    }

    const observer = new ResizeObserver(update);
    navbars.forEach((el) => observer.observe(el));

    update();
}

// ── Navbar Initialisation ──────────────────────────────────────────────────
function initNavbar() {
    const desktopLang = initDropdown('langBtn', 'langDropdown');
    const mobileLang  = initDropdown('mobileLangBtn', 'mobileLangDropdown');

    initMobileDrawer();
    initScrollSpy();
    initNavbarHeight();

    // Close all language dropdowns on outside click or Escape.
    function closeAll() {
        if (desktopLang) closeDropdown(desktopLang);
        if (mobileLang)  closeDropdown(mobileLang);
    }

    document.addEventListener('click', closeAll);
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeAll();
    });
}

document.addEventListener('DOMContentLoaded', () => {
    initNavbar();
});
